// Map peek popover (T11) — the spatial analogue of JsonPeekPopover. The grid's ⌖ cell glyph is the
// trigger; clicking it opens a Radix popover with the decoded mini map + summary + Copy WKT/GeoJSON,
// plus an "Open full view" link into the expanded-dialog map pane. Windowed grids hand the peek a
// 1 KB preview value + a `fetchFullValue` closure (T26 bounded batch) so the FULL geometry is decoded
// on open, never the truncated preview. Over-cap values surface the "Load full geometry" ladder
// (inside SpatialGeometryPreview) rather than auto-decoding.

import { useState } from 'react';
import { Maximize2 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import { useAnalytics } from '../../../../../core/analytics';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import { sniffSpatialValue } from '../../../spatial/decode/spatialSniff';
import type { SpatialColumnDeclaration } from '../../utils/spatialValueAccessor';
import { SpatialGeometryPreview } from '../../../spatial/map/SpatialGeometryPreview';

export type SpatialPeekPopoverProps = {
  value: unknown;
  engine: DatabaseEngineId;
  dataType?: string;
  /** T18 — when set, the value is a declared coordinate cell decoded via the declared arm. */
  declaration?: SpatialColumnDeclaration;
  columnName: string;
  trigger: React.ReactNode;
  /** Opens the expanded-dialog map pane for this cell. */
  onOpenFull?: () => void;
  /** Windowed grids only: fetch the FULL untruncated value (T26 bounded batch). */
  fetchFullValue?: () => Promise<unknown>;
  /** Resolve non-bundled SRIDs from this connection's `spatial_ref_sys` (T12 ladder). */
  connectionId?: string;
  database?: string;
};

export function SpatialPeekPopover({
  value,
  engine,
  dataType,
  declaration,
  columnName,
  trigger,
  onOpenFull,
  fetchFullValue,
  connectionId,
  database,
}: SpatialPeekPopoverProps) {
  const [open, setOpen] = useState(false);
  const { track } = useAnalytics();

  const peekGeomType = declaration
    ? declaration.format === 'lat-lng-pair' || declaration.format === 'latlng-text'
      ? 'Point'
      : 'Declared'
    : sniffSpatialValue(engine, value, dataType)?.geomType ?? 'Unknown';

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          track('feature:spatial_peek_open', { engine, geomType: peekGeomType });
        }
      }}
    >
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className="w-72 p-2"
        align="start"
        data-testid="spatial-peek-popover"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span className="truncate text-xs font-medium text-foreground" title={columnName}>
            {columnName}
          </span>
          {onOpenFull && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => {
                setOpen(false);
                onOpenFull();
              }}
              data-testid="spatial-peek-open-full"
            >
              <Maximize2 className="h-3 w-3" aria-hidden="true" />
              Open full view
            </button>
          )}
        </div>
        <SpatialGeometryPreview
          value={value}
          engine={engine}
          dataType={dataType}
          declaration={declaration}
          fetchFullValue={fetchFullValue}
          enabled={open}
          showCopy
          compactAttribution
          connectionId={connectionId}
          database={database}
          onCopy={(format) => track('feature:spatial_copy', { format })}
        />
      </PopoverContent>
    </Popover>
  );
}
